import { useRef, useCallback } from 'react'
import { useTuningStore, getCentsForMidiNote } from '../store/tuningStore'
import { calculateFrequencyFromTonic } from '../utils/noteUtils'

interface PlayingNote {
  source: AudioBufferSourceNode
  gainNode: GainNode
  noteId: string
}

// Available sample range: MIDI notes 57-72
const SAMPLE_MIN = 57
const SAMPLE_MAX = 72

export const useAudio = () => {
  const audioContextRef = useRef<AudioContext | null>(null)
  const buffersRef = useRef<Map<number, AudioBuffer>>(new Map())
  const playingNotesRef = useRef<Map<string, PlayingNote>>(new Map())
  const loadingPromisesRef = useRef<Map<number, Promise<AudioBuffer>>>(new Map())
  const samplesLoadedRef = useRef<Set<number>>(new Set())
  const { tonicNote, tonicFrequency, referencePitch } = useTuningStore()

  // Find equivalent note in available sample range (57-72)
  // Returns the sample note to use and the octave offset in semitones
  const findEquivalentSample = useCallback((midiNote: number): { sampleNote: number; octaveOffset: number } => {
    if (midiNote >= SAMPLE_MIN && midiNote <= SAMPLE_MAX) {
      // Note is in range, use it directly
      return { sampleNote: midiNote, octaveOffset: 0 }
    }
    
    // Find equivalent note in the available range
    const noteInOctave = midiNote % 12 // Position in octave (0-11)
    
    // First, try to find a note with the same position in octave
    let bestSample: number | null = null
    let bestDistance = Infinity
    
    for (let note = SAMPLE_MIN; note <= SAMPLE_MAX; note++) {
      if (note % 12 === noteInOctave) {
        const distance = Math.abs(midiNote - note)
        if (distance < bestDistance) {
          bestDistance = distance
          bestSample = note
        }
      }
    }
    
    // If no exact match found (same note in octave), use the closest note in range
    if (bestSample === null) {
      for (let note = SAMPLE_MIN; note <= SAMPLE_MAX; note++) {
        const distance = Math.abs(midiNote - note)
        if (distance < bestDistance) {
          bestDistance = distance
          bestSample = note
        }
      }
    }
    
    // Fallback to middle of range if something went wrong
    if (bestSample === null) {
      bestSample = Math.floor((SAMPLE_MIN + SAMPLE_MAX) / 2)
    }
    
    const octaveOffset = midiNote - bestSample
    return { sampleNote: bestSample, octaveOffset }
  }, [])

  const getAudioContext = useCallback((): AudioContext => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
    }
    return audioContextRef.current
  }, [])

  const resumeContext = useCallback(async (): Promise<void> => {
    const ctx = getAudioContext()
    if (ctx.state === 'suspended') {
      await ctx.resume()
    }
  }, [getAudioContext])


  const loadSample = useCallback(
    async (midiNote: number): Promise<AudioBuffer> => {
      // Ensure note is in available range (57-72)
      // This should already be handled by findEquivalentSample, but add safety check
      const clampedNote = Math.max(SAMPLE_MIN, Math.min(SAMPLE_MAX, midiNote))
      
      // Check cache first
      if (buffersRef.current.has(clampedNote)) {
        return buffersRef.current.get(clampedNote)!
      }

      // If already loading, return the existing promise
      if (loadingPromisesRef.current.has(clampedNote)) {
        return loadingPromisesRef.current.get(clampedNote)!
      }

      // Create loading promise
      const loadPromise = (async (): Promise<AudioBuffer> => {
        // Try to load sample from public/samples directory
        // With Vite, files in public/ are served at root, so /samples/ is correct
        const samplePath = `/samples/note_${clampedNote}.wav`
        
        try {
          const response = await fetch(samplePath)
          
          if (!response.ok) {
            throw new Error(`Sample not found or invalid: HTTP ${response.status}`)
          }
          
          // Try to decode the audio data - this is the real validation
          // Don't rely solely on content-type header as it may not be set correctly in all environments
          const arrayBuffer = await response.arrayBuffer()
          const audioBuffer = await getAudioContext().decodeAudioData(arrayBuffer)
          buffersRef.current.set(clampedNote, audioBuffer)
          samplesLoadedRef.current.add(clampedNote)
          console.log(`✅ Sample loaded: note_${clampedNote}.wav (${(arrayBuffer.byteLength / 1024).toFixed(1)}KB)`)
          return audioBuffer
        } catch (error) {
          console.error(`❌ Error loading sample: ${error}`)
          // Sample not found - throw error instead of using fallback
          console.error(`❌ Sample not found: ${samplePath}`)
          console.error(`   Please place note_${clampedNote}.wav in public/samples/ directory`)
          throw new Error(`Sample not found for note ${clampedNote}. Please add note_${clampedNote}.wav to public/samples/`)
        }
      })()

      // Store the promise to prevent duplicate loads
      loadingPromisesRef.current.set(clampedNote, loadPromise)
      
      // Clean up promise after completion
      loadPromise.finally(() => {
        loadingPromisesRef.current.delete(clampedNote)
      })

      return loadPromise
    },
    [getAudioContext]
  )

  const playNote = useCallback(
    async (midiNote: number, cents: number = 0) => {
      await resumeContext()

      const ctx = getAudioContext()
      const noteId = `note_${midiNote}_${Date.now()}`

      // Stop any existing note with same MIDI note
      const existingNote = Array.from(playingNotesRef.current.values()).find(
        (n) => n.noteId.startsWith(`note_${midiNote}_`)
      )
      if (existingNote) {
        existingNote.source.stop()
        playingNotesRef.current.delete(existingNote.noteId)
      }

      try {
        // Find equivalent sample in available range (57-72)
        const { sampleNote, octaveOffset } = findEquivalentSample(midiNote)
        const buffer = await loadSample(sampleNote)

        const source = ctx.createBufferSource()
        const gainNode = ctx.createGain()

        // Calculate playback rate for pitch shifting
        // Combine cents offset with octave offset (in semitones)
        // octaveOffset is in semitones, convert to cents: octaveOffset * 100
        const totalCents = cents + (octaveOffset * 100)
        const playbackRate = Math.pow(2, totalCents / 1200)
        source.buffer = buffer
        source.playbackRate.value = playbackRate

        // ADSR Envelope
        const now = ctx.currentTime
        const attackTime = 0.01
        const decayTime = 0.1
        const sustainLevel = 0.7
        const releaseTime = 0.2

        gainNode.gain.setValueAtTime(0, now)
        gainNode.gain.linearRampToValueAtTime(1, now + attackTime)
        gainNode.gain.linearRampToValueAtTime(sustainLevel, now + attackTime + decayTime)
        gainNode.gain.setValueAtTime(sustainLevel, now + buffer.duration - releaseTime)
        gainNode.gain.linearRampToValueAtTime(0, now + buffer.duration)

        source.connect(gainNode)
        gainNode.connect(ctx.destination)

        source.onended = () => {
          playingNotesRef.current.delete(noteId)
        }

        source.start(0)
        playingNotesRef.current.set(noteId, { source, gainNode, noteId })
      } catch (error) {
        console.error(`❌ Cannot play note ${midiNote}:`, error instanceof Error ? error.message : error)
        // Don't play anything if sample is missing
      }
    },
    [resumeContext, getAudioContext, loadSample, findEquivalentSample]
  )

  const stopNote = useCallback((noteId: string) => {
    const playingNote = playingNotesRef.current.get(noteId)
    if (playingNote) {
      const ctx = getAudioContext()
      const now = ctx.currentTime
      const releaseTime = 0.1

      // Smooth release
      playingNote.gainNode.gain.cancelScheduledValues(now)
      playingNote.gainNode.gain.setValueAtTime(playingNote.gainNode.gain.value, now)
      playingNote.gainNode.gain.linearRampToValueAtTime(0, now + releaseTime)

      setTimeout(() => {
        playingNote.source.stop()
        playingNotesRef.current.delete(noteId)
      }, releaseTime * 1000)
    }
  }, [getAudioContext])

  const playNoteWithTuning = useCallback(
    async (midiNote: number) => {
      try {
        await resumeContext() // Ensure audio context is resumed
        
        const offset = getCentsForMidiNote(midiNote)

        // Calculate target frequency based on tonic
        const targetFrequency = calculateFrequencyFromTonic(
          midiNote,
          offset,
          tonicNote,
          tonicFrequency,
          referencePitch
        )
        
        // Calculate standard frequency for this MIDI note (A4 = 440 Hz)
        // Standard frequency = 440 * 2^((midiNote - 69) / 12)
        const standardFrequency = 440 * Math.pow(2, (midiNote - 69) / 12)
        
        // Calculate playback rate to achieve target frequency
        // playbackRate = targetFrequency / standardFrequency
        const playbackRate = targetFrequency / standardFrequency
        
        // Convert playback rate to cents for the playNote function
        // playbackRate = 2^(cents/1200), so cents = 1200 * log2(playbackRate)
        const totalCents = 1200 * Math.log2(playbackRate)
        
        console.log(`🎵 Playing note ${midiNote}: target=${targetFrequency.toFixed(2)}Hz, standard=${standardFrequency.toFixed(2)}Hz, rate=${playbackRate.toFixed(4)}, cents=${totalCents.toFixed(2)}`)
        
        await playNote(midiNote, totalCents)
      } catch (error) {
        console.error(`❌ Error in playNoteWithTuning for note ${midiNote}:`, error)
        throw error
      }
    },
    [playNote, tonicNote, tonicFrequency, referencePitch, resumeContext]
  )

  // Preload samples for available MIDI notes (57-72)
  const preloadSamples = useCallback(
    async (midiNotes?: number[]) => {
      await resumeContext()
      
      // If no notes specified, preload all available samples (57-72)
      const notesToLoad = midiNotes || Array.from({ length: SAMPLE_MAX - SAMPLE_MIN + 1 }, (_, i) => i + SAMPLE_MIN)
      
      console.log(`🔄 Preloading samples for ${notesToLoad.length} notes (${notesToLoad[0]} to ${notesToLoad[notesToLoad.length - 1]})...`)
      
      // Load in batches to avoid overwhelming the browser
      const batchSize = 20
      let loaded = 0
      let failed = 0
      
      for (let i = 0; i < notesToLoad.length; i += batchSize) {
        const batch = notesToLoad.slice(i, i + batchSize)
        const results = await Promise.allSettled(batch.map((note) => loadSample(note)))
        
        loaded += results.filter(r => r.status === 'fulfilled').length
        failed += results.filter(r => r.status === 'rejected').length
      }
      
      console.log(`✅ Sample preloading complete: ${loaded} loaded, ${failed} failed`)
      if (failed > 0) {
        console.warn(`⚠️ ${failed} samples are missing. Place WAV files in public/samples/ (e.g., note_12.wav, note_13.wav, etc.)`)
      }
    },
    [loadSample, resumeContext]
  )

  const hasSample = useCallback((midiNote: number): boolean => {
    const { sampleNote } = findEquivalentSample(midiNote)
    return samplesLoadedRef.current.has(sampleNote)
  }, [findEquivalentSample])

  return {
    loadSample,
    playNote,
    playNoteWithTuning,
    stopNote,
    resumeContext,
    preloadSamples,
    hasSample
  }
}

